import React, {FC} from 'react'

// Interface for inventory sync response
interface InventoryChange {
  productName: string;
  variantName: string;
  beforeInventory: number;
  updatedInventory: number;
  status: 'success' | 'error';
  errorMessage?: string;
}

interface StoreBreakdown {
  totalVariants: number;
  successfulUpdates: number;
  failedUpdates: number;
  inventoryChanges: InventoryChange[];
}

interface SyncSummary {
  productsProcessed: number;
  variantsUpdated: number;
  shopifyUpdates: number;
  databaseUpdates: number;
  storesAffected: string[];
  totalErrors: number;
  scrapeErrors: number;
  shopifyErrors: number;
  databaseErrors: number;
  productsDisconnected?: number;
}

interface DisconnectedProduct {
  productId: number;
  productTitle: string;
  shopifyProductId: number;
  storeName: string;
  reason: string;
  variantsDisconnected: number;
}

interface SyncResponse {
  message: string;
  summary: SyncSummary;
  storeBreakdown: Record<string, StoreBreakdown>;
  errors: {
    scrapeErrors: string[];
    shopifyErrors: string[];
    databaseErrors: string[];
  };
  disconnectedProducts?: DisconnectedProduct[];
}

interface KangerWholesaleUpdateModalProps {
  show: boolean;
  onClose: () => void;
  syncData?: SyncResponse;
  loading: boolean;
  syncLogs: string[];
}

export const KangerWholesaleUpdateModal: FC<KangerWholesaleUpdateModalProps> = ({
  show,
  onClose,
  syncData,
  loading,
  syncLogs,
}) => {
  if (!show) return null;

  // Filter inventory changes to only show items where inventory actually changed
  const getChangedInventory = (changes: InventoryChange[]) => {
    return changes.filter(change => 
      change.status === 'success' && change.beforeInventory !== change.updatedInventory
    );
  };

  // Get errors for a specific store
  const getStoreErrors = (storeName: string, changes: InventoryChange[]) => {
    return changes.filter(change => change.status === 'error');
  };

  return (
    <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999}}>
      <div className='modal-dialog modal-xl modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header border-0 pb-0'>
            <h4 className='modal-title fw-bold text-primary'>
              <i className='fas fa-sync-alt me-2'></i>
              Inventory Sync Results
            </h4>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          
          <div className='modal-body px-4'>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary mb-3' style={{width: '3rem', height: '3rem'}} role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <h5 className='text-muted'>Syncing inventory...</h5>
                <p className='text-muted mb-2'>
                  Please wait while we update inventory across all connected stores. 
                  This may take a few minutes. You can see the progress below.
                </p>
                <div 
                  className='card-body text-white p-3 rounded mt-3 text-start' 
                  style={{maxHeight: '400px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem',backgroundColor:'black'}}
                >
                  {syncLogs.map((log, i) => <div key={i}>{log}</div>)}
                </div>
              </div>
            ) : syncData ? (
              <>
                {/* Disconnected Products Alert */}
                {syncData.disconnectedProducts && syncData.disconnectedProducts.length > 0 && (
                  <div className='alert alert-warning mb-4'>
                    <div className='d-flex align-items-center mb-3'>
                      <i className='fas fa-unlink fs-4 me-3'></i>
                      <div>
                        <h5 className='mb-0'>Products Disconnected from Shopify</h5>
                        <small className='text-muted'>
                          {syncData.disconnectedProducts.length} product{syncData.disconnectedProducts.length > 1 ? 's were' : ' was'} automatically disconnected because {syncData.disconnectedProducts.length > 1 ? 'they were' : 'it was'} deleted from Shopify
                        </small>
                      </div>
                    </div>
                    <div className='accordion' id='disconnectedAccordion'>
                      <div className='accordion-item border-0'>
                        <h2 className='accordion-header' id='headingDisconnected'>
                          <button
                            className='accordion-button collapsed py-2'
                            type='button'
                            data-bs-toggle='collapse'
                            data-bs-target='#collapseDisconnected'
                            aria-expanded='false'
                            aria-controls='collapseDisconnected'
                          >
                            <small>View disconnected products</small>
                          </button>
                        </h2>
                        <div
                          id='collapseDisconnected'
                          className='accordion-collapse collapse'
                          aria-labelledby='headingDisconnected'
                          data-bs-parent='#disconnectedAccordion'
                        >
                          <div className='accordion-body pt-0'>
                            <div className='table-responsive'>
                              <table className='table table-sm mb-0'>
                                <thead>
                                  <tr>
                                    <th>Product</th>
                                    <th>Store</th>
                                    <th>Variants</th>
                                    <th>Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {syncData.disconnectedProducts.map((product, index) => (
                                    <tr key={index}>
                                      <td className='fw-medium'>
                                        {product.productTitle}
                                        <br/>
                                        <small className='text-muted'>ID: {product.productId}</small>
                                      </td>
                                      <td>
                                        <span className='badge bg-light text-dark'>{product.storeName}</span>
                                      </td>
                                      <td className='text-center'>
                                        <span className='badge bg-secondary'>{product.variantsDisconnected}</span>
                                      </td>
                                      <td>
                                        <small className='text-danger'>{product.reason}</small>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Overall Summary Stats */}
                <div className='row mb-4'>
                  <div className='col-12'>
                    <div className='card border-0 shadow-sm'>
                      <div className='card-body p-4'>
                        <h5 className='card-title text-dark mb-3'>
                          <i className='fas fa-chart-bar me-2 text-primary'></i>
                          Sync Summary
                        </h5>
                        <div className='row g-3'>
                          <div className='col-md-3 col-sm-6'>
                            <div className='d-flex align-items-center'>
                              <div className='flex-shrink-0'>
                                <div className='bg-primary rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                  <i className='fas fa-boxes text-white'></i>
                                </div>
                              </div>
                              <div className='flex-grow-1 ms-3'>
                                <div className='fw-bold fs-4 text-primary'>{syncData.summary.productsProcessed}</div>
                                <div className='text-muted small'>Products Processed</div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-sm-6'>
                            <div className='d-flex align-items-center'>
                              <div className='flex-shrink-0'>
                                <div className='bg-success rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                  <i className='fas fa-check text-white'></i>
                                </div>
                              </div>
                              <div className='flex-grow-1 ms-3'>
                                <div className='fw-bold fs-4 text-success'>{syncData.summary.shopifyUpdates}</div>
                                <div className='text-muted small'>Shopify Updates</div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-sm-6'>
                            <div className='d-flex align-items-center'>
                              <div className='flex-shrink-0'>
                                <div className='bg-info rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                  <i className='fas fa-store text-white'></i>
                                </div>
                              </div>
                              <div className='flex-grow-1 ms-3'>
                                <div className='fw-bold fs-4 text-info'>{syncData.summary.storesAffected.length}</div>
                                <div className='text-muted small'>Stores Affected</div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-sm-6'>
                            <div className='d-flex align-items-center'>
                              <div className='flex-shrink-0'>
                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${syncData.summary.totalErrors > 0 ? 'bg-warning' : 'bg-success'}`} style={{width: '40px', height: '40px'}}>
                                  <i className={`fas ${syncData.summary.totalErrors > 0 ? 'fa-exclamation-triangle' : 'fa-check'} text-white`}></i>
                                </div>
                              </div>
                              <div className='flex-grow-1 ms-3'>
                                <div className={`fw-bold fs-4 ${syncData.summary.totalErrors > 0 ? 'text-warning' : 'text-success'}`}>
                                  {syncData.summary.totalErrors}
                                </div>
                                <div className='text-muted small'>Total Errors</div>
                              </div>
                            </div>
                          </div>
                          {syncData.summary.productsDisconnected !== undefined && syncData.summary.productsDisconnected > 0 && (
                            <div className='col-md-3 col-sm-6'>
                              <div className='d-flex align-items-center'>
                                <div className='flex-shrink-0'>
                                  <div className='bg-warning rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                    <i className='fas fa-unlink text-white'></i>
                                  </div>
                                </div>
                                <div className='flex-grow-1 ms-3'>
                                  <div className='fw-bold fs-4 text-warning'>{syncData.summary.productsDisconnected}</div>
                                  <div className='text-muted small'>Disconnected</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Status message */}
                        <div className='mt-3'>
                          <div className={`alert ${syncData.summary.totalErrors > 0 ? 'alert-warning' : 'alert-success'} mb-0`}>
                            <i className={`fas ${syncData.summary.totalErrors > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2`}></i>
                            {syncData.message}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Store-by-Store Breakdown */}
                {Object.entries(syncData.storeBreakdown).map(([storeName, breakdown]) => {
                  const changedInventory = getChangedInventory(breakdown.inventoryChanges);
                  const storeErrors = getStoreErrors(storeName, breakdown.inventoryChanges);
                  
                  return (
                    <div key={storeName} className='mb-4'>
                      <div className='card border-0 shadow-sm'>
                        <div className='card-header border-0 pb-0'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <h5 className='mb-0 fw-bold text-dark'>
                              <i className='fas fa-store me-2 text-primary'></i>
                              {storeName}
                            </h5>
                            <div className='d-flex gap-3'>
                              <span className='badge bg-success fs-6'>{breakdown.successfulUpdates} Success</span>
                              {breakdown.failedUpdates > 0 && (
                                <span className='badge bg-danger fs-6'>{breakdown.failedUpdates} Failed</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className='card-body'>
                          <div className='accordion accordion-flush' id={`accordion-${storeName}`}>
                            {/* Inventory Changes Accordion */}
                            {changedInventory.length > 0 ? (
                              <div className='accordion-item'>
                                <h2 className='accordion-header' id={`heading-changes-${storeName}`}>
                                  <button
                                    className='accordion-button collapsed'
                                    type='button'
                                    data-bs-toggle='collapse'
                                    data-bs-target={`#collapse-changes-${storeName}`}
                                    aria-expanded='false'
                                    aria-controls={`collapse-changes-${storeName}`}
                                  >
                                    <i className='fas fa-arrows-alt-v me-2 text-success'></i>
                                    Inventory Changes ({changedInventory.length})
                                  </button>
                                </h2>
                                <div
                                  id={`collapse-changes-${storeName}`}
                                  className='accordion-collapse collapse'
                                  aria-labelledby={`heading-changes-${storeName}`}
                                  data-bs-parent={`#accordion-${storeName}`}
                                >
                                  <div className='accordion-body p-0'>
                                    <div className='table-responsive'>
                                      <table className='table table-hover table-sm mb-0'>
                                        <thead className='table-dark'>
                                          <tr>
                                            <th className='fw-semibold'>Product</th>
                                            <th className='fw-semibold'>Variant</th>
                                            <th className='fw-semibold text-center'>Before</th>
                                            <th className='fw-semibold text-center'>After</th>
                                            <th className='fw-semibold text-center'>Change</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {changedInventory.map((change, index) => {
                                            const diff = change.updatedInventory - change.beforeInventory;
                                            return (
                                              <tr key={index}>
                                                <td className='fw-medium'>{change.productName}</td>
                                                <td>
                                                  <span className='badge bg-light text-dark'>{change.variantName}</span>
                                                </td>
                                                <td className='text-center'>
                                                  <span className='badge bg-secondary'>{change.beforeInventory}</span>
                                                </td>
                                                <td className='text-center'>
                                                  <span className='badge bg-primary'>{change.updatedInventory}</span>
                                                </td>
                                                <td className='text-center'>
                                                  <span className={`badge ${diff > 0 ? 'bg-success' : diff < 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                    {diff > 0 ? '+' : ''}{diff}
                                                  </span>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className='text-center py-3'>
                                <i className='fas fa-info-circle text-muted me-2'></i>
                                <span className='text-muted'>No inventory changes detected for this store</span>
                              </div>
                            )}

                            {/* Errors Accordion */}
                            {storeErrors.length > 0 && (
                              <div className='accordion-item'>
                                <h2 className='accordion-header' id={`heading-errors-${storeName}`}>
                                  <button
                                    className='accordion-button collapsed'
                                    type='button'
                                    data-bs-toggle='collapse'
                                    data-bs-target={`#collapse-errors-${storeName}`}
                                    aria-expanded='false'
                                    aria-controls={`collapse-errors-${storeName}`}
                                  >
                                    <i className='fas fa-exclamation-triangle me-2 text-danger'></i>
                                    Errors ({storeErrors.length})
                                  </button>
                                </h2>
                                <div
                                  id={`collapse-errors-${storeName}`}
                                  className='accordion-collapse collapse'
                                  aria-labelledby={`heading-errors-${storeName}`}
                                  data-bs-parent={`#accordion-${storeName}`}
                                >
                                  <div className='accordion-body p-0'>
                                    <div className='table-responsive'>
                                      <table className='table table-sm  table-striped mb-0'>
                                        <thead>
                                          <tr>
                                            <th className='fw-semibold'>Product</th>
                                            <th className='fw-semibold'>Variant</th>
                                            <th className='fw-semibold'>Error Message</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {storeErrors.map((error, index) => (
                                            <tr key={index}>
                                              <td className='fw-medium'>{error.productName}</td>
                                              <td>
                                                <span className='badge bg-light text-dark'>{error.variantName}</span>
                                              </td>
                                              <td>
                                                <small className='text-danger'>{error.errorMessage}</small>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className='text-center py-5'>
                <i className='fas fa-exclamation-circle text-muted mb-3' style={{fontSize: '3rem'}}></i>
                <h5 className='text-muted'>No sync data available</h5>
              </div>
            )}
          </div>
          
          <div className='modal-footer border-0 pt-0'>
            <button type='button' className='btn btn-secondary' onClick={onClose}>
              <i className='fas fa-times me-2'></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
